import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Target, Plus, Minus, Sparkles, Award } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api from "../lib/api.js";

const MIN = 1_000_000;
const MAX = 50_000_000;
const PRESETS = [1, 2, 5, 10, 25]; // in millions

export default function AnnualGoal() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(MIN);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/annual-goal");
    setData(r.data);
    setTarget(r.data.target_steps);
  };
  useEffect(() => { load(); }, []);

  const bump = (delta) => {
    setTarget((t) => Math.max(MIN, Math.min(MAX, t + delta)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/annual-goal", { target_steps: target });
      setData(r.data);
      toast.success(`Annual goal set to ${target.toLocaleString()} steps`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save goal");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <PhoneFrame><div className="p-6 text-slate-400">Loading…</div></PhoneFrame>;

  const pct = Math.min(100, data.percent_complete);

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="annual-back">
          <ChevronLeft size={16} /> Back
        </button>

        <div className="mt-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#142534] flex items-center justify-center">
            <Target size={22} color="#F59E0B" />
          </div>
          <div>
            <h1 className="ps-heading text-2xl font-bold">Annual Step Goal</h1>
            <p className="text-slate-400 text-sm">Your {data.year} target. Range 1M–50M steps.</p>
          </div>
        </div>

        {/* Current progress summary */}
        <div className="mt-5 rounded-3xl p-6 bg-gradient-to-br from-[#F59E0B]/15 to-[#2fbf71]/10 border border-[#F59E0B]/30">
          <div className="text-[10px] uppercase tracking-widest text-[#F59E0B] font-bold">
            Current target {data.target_steps.toLocaleString()}
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <div className="ps-heading text-4xl font-bold" data-testid="annual-remaining">
                {data.remaining_steps.toLocaleString()}
              </div>
              <div className="text-xs text-slate-300 mt-1">steps remaining</div>
            </div>
            <div className="text-right">
              <div className="ps-heading text-4xl font-bold ps-gradient-text" data-testid="annual-percent">
                {pct.toFixed(1)}%
              </div>
              <div className="text-xs text-slate-300 mt-1">complete</div>
            </div>
          </div>
          <div className="mt-4 h-3 rounded-full bg-[#0e1d2a]/60">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#F59E0B] via-[#2fbf71] to-[#00B4D8]"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Set new goal */}
        <div className="mt-6 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Set your goal</div>
          <div className="mt-3 flex items-center justify-center gap-3">
            <button
              data-testid="annual-decrement"
              onClick={() => bump(-500_000)}
              disabled={target <= MIN}
              className="w-12 h-12 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center disabled:opacity-40"
              aria-label="Decrease 500K"
            ><Minus size={18} /></button>
            <div className="flex-1 text-center">
              <div className="ps-heading text-4xl font-bold text-[#2fbf71]" data-testid="annual-target-preview">
                {target.toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 mt-1">steps / year</div>
            </div>
            <button
              data-testid="annual-increment"
              onClick={() => bump(500_000)}
              disabled={target >= MAX}
              className="w-12 h-12 rounded-full bg-[#142534] border border-[#1a2f40] flex items-center justify-center disabled:opacity-40"
              aria-label="Increase 500K"
            ><Plus size={18} /></button>
          </div>
          <input
            data-testid="annual-slider"
            type="range" min={MIN} max={MAX} step="500000"
            value={Math.min(target, MAX)}
            onChange={(e) => setTarget(+e.target.value)}
            className="ps-slider mt-4"
          />
          <div className="flex justify-between text-[10px] text-slate-500 mt-1">
            <span>1M</span><span>25M</span><span>50M</span>
          </div>

          <div className="mt-4 grid grid-cols-5 gap-2">
            {PRESETS.map((m) => (
              <button
                key={m}
                data-testid={`annual-preset-${m}m`}
                onClick={() => setTarget(m * 1_000_000)}
                className={`py-2 rounded-xl text-xs font-bold border ${
                  target === m * 1_000_000
                    ? "bg-[#2fbf71] text-[#0a1822] border-transparent"
                    : "border-[#1a2f40] text-slate-300"
                }`}
              >{m}M</button>
            ))}
          </div>

          <button
            data-testid="annual-save"
            onClick={save}
            disabled={saving || target === data.target_steps}
            className="ps-button-primary w-full mt-5"
          >
            {saving ? "Saving…" : target === data.target_steps ? "No changes" : "Save goal"}
          </button>
        </div>

        {/* Milestones */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Milestone badges</div>
          <div className="space-y-2">
            {data.milestones.map((m) => (
              <div
                key={m.code}
                data-testid={`annual-ms-${m.pct}`}
                className={`ps-card p-4 flex items-center gap-3 ${m.achieved ? "border-[#2fbf71]/40" : "opacity-70"}`}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold"
                  style={{ background: m.achieved ? m.color : "#142534", color: m.achieved ? "#0a1822" : m.color }}
                >
                  {m.pct >= 100 ? "★" : `${m.pct}%`}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {m.title}
                    {m.achieved && <Award size={13} color="#2fbf71" />}
                  </div>
                  <div className="text-xs text-slate-400">
                    {m.absolute_steps.toLocaleString()} steps · +{100 + m.pct * 2} bonus pts
                  </div>
                </div>
                {!m.achieved && <Sparkles size={14} className="text-slate-500" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
